import {
  DollarCircleFilled,
  EllipsisOutlined,
  HomeFilled,
  MoreOutlined,
  PieChartFilled,
  QuestionCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Dropdown, Menu } from "antd";
import type { MenuProps } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../providers/auth.provider";
import { capitalize, capitalizeFirst } from "../utils/functions";
import GroupSelectComponent from "./group.select.component";

export default function Header() {
  const navigate = useNavigate();
  const { user, signout } = useAuthContext();
  const moreItems = [
    {
      key: "0",
      icon: <EllipsisOutlined />,
      children: [
        {
          key: "income",
          label: "Income",
          onClick: () => {
            navigate("/incomes");
          },
        },
        {
          key: "1",
          label: "Ledgers",
          onClick: () => {
            navigate("/ledgers");
          },
        },
        {
          key: "11",
          label: "Misc Expenses",
          onClick: () => {
            navigate("/misc");
          },
        },
        {
          key: "2",
          label: "Trips",
          onClick: () => {
            navigate("/trips");
          },
        },
        {
          key: "3",
          label: "Accounts",
          onClick: () => {
            navigate("/accounts");
          },
        },
        {
          key: "4",
          label: "Groups",
          onClick: () => {
            navigate("/groups");
          },
        },
        {
          key: "5",
          label: "Subscriptions",
          onClick: () => {
            navigate("/subscriptions");
          },
        },
      ],
    },
  ];

  const items = [
    {
      key: "0",
      icon: (
        <div>
          <Avatar style={{ backgroundColor: "#f56a00" }}>
            {capitalizeFirst(
              user?.firstname
                ? user?.firstname.charAt(0)
                : user?.email.charAt(0),
            )}
          </Avatar>
        </div>
      ),
      children: [
        {
          key: "1",
          label: "Profile",
          onClick: () => {
            navigate(`/profile`);
          },
        },
        {
          key: "2",
          label: "Groups",
          onClick: () => {
            navigate("/groups");
          },
        },
        {
          key: "3",
          label: "Subscriptions",
          onClick: () => {
            navigate("/subscriptions");
          },
        },
        {
          key: "31",
          label: "Notifications",
          onClick: () => {
            navigate("/notifications");
          },
        },
        {
          key: "5",
          label: "Help",
          onClick: () => {
            navigate("/help");
          },
        },
        {
          key: "4",
          label: "Sign Out",
          onClick: () => {
            signout();
          },
        },
      ],
    },
  ];

  const mobileMenuItems: MenuProps["items"] = [
    { key: "home", label: "Home", onClick: () => navigate("/home") },
    {
      key: "analysis",
      label: "Analysis",
      onClick: () => navigate("/analysis"),
    },
    { key: "savings", label: "Savings", onClick: () => navigate("/savings") },
    { key: "search", label: "Search", onClick: () => navigate("/search") },
    { type: "divider" },
    { key: "income", label: "Income", onClick: () => navigate("/incomes") },
    { key: "ledgers", label: "Ledgers", onClick: () => navigate("/ledgers") },
    { key: "misc", label: "Misc Expenses", onClick: () => navigate("/misc") },
    { key: "trips", label: "Trips", onClick: () => navigate("/trips") },
    {
      key: "accounts",
      label: "Accounts",
      onClick: () => navigate("/accounts"),
    },
    { key: "groups", label: "Groups", onClick: () => navigate("/groups") },
    { type: "divider" },
    { key: "profile", label: "Profile", onClick: () => navigate("/profile") },
    {
      key: "subscriptions",
      label: "Subscriptions",
      onClick: () => navigate("/subscriptions"),
    },
    {
      key: "notifications",
      label: "Notifications",
      onClick: () => navigate("/notifications"),
    },
    { key: "help", label: "Help", onClick: () => navigate("/help") },
    { type: "divider" },
    {
      key: "signout",
      label: "Sign Out",
      onClick: () => signout(),
      danger: true,
    },
  ];
  return (
    <header className="w-full bg-white/80 backdrop-blur-md border-b border-border z-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex gap-2">
            <Link
              to={!!user ? "/home" : "/"}
              className="flex items-center gap-2"
            >
              <Avatar src="/assets/logo.png" alt="logo" />
              <span className="font-bold text-xl text-foreground hidden sm:inline">
                ExpenseHut
              </span>
            </Link>
          </div>

          {/* Navigation Links */}
          {!user && (
            <>
              <nav className="hidden md:flex items-center gap-8">
                <a
                  href="/#features"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition"
                >
                  Features
                </a>
                <a
                  href="/#how-it-works"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition"
                >
                  How it Works
                </a>
                <a
                  href="/#benefits"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition"
                >
                  Why ExpenseHut
                </a>
              </nav>

              <Button type="primary" onClick={() => navigate("/login")}>
                Get Started
              </Button>
            </>
          )}

          {!!user && (
            <>
              <nav className="hidden lg:flex items-center gap-8">
                <Link
                  to="/home"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition gap-1 flex items-center"
                >
                  <HomeFilled />
                  Home
                </Link>
                <Link
                  to="/analysis"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition gap-1 flex items-center"
                >
                  <PieChartFilled />
                  Analysis
                </Link>
                <Link
                  to="/savings"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition gap-1 flex items-center"
                >
                  <DollarCircleFilled />
                  Savings
                </Link>
                <Link
                  to="/search"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition gap-1 flex items-center"
                >
                  <SearchOutlined />
                  Search
                </Link>
                <Link
                  to="/help"
                  className="text-sm font-medium text-blue-500 hover:text-blue-700 transition gap-1 flex items-center"
                >
                  <QuestionCircleOutlined />
                  Help
                </Link>
                <Menu
                  id="home-options-menu"
                  style={{
                    width: 80,
                    backgroundColor: "inherit",
                    color: "white",
                  }}
                  mode="horizontal"
                  items={moreItems}
                />
              </nav>
            </>
          )}

          {!!user && (
            <div className="flex items-center gap-3">
              {/* Group switcher — visible on all breakpoints */}
              <div className="block">
                <GroupSelectComponent />
              </div>

              {/* User name */}
              <Link
                to="/profile"
                className="font-semibold text-blue-500 hover:text-blue-700 underline text-sm hidden sm:block transition"
              >
                Hi, {capitalize(user?.firstname ?? "guest")}
              </Link>

              {/* Desktop: avatar opens profile/account menu */}
              <div className="hidden lg:block">
                <Menu
                  id="home-options-menu"
                  style={{
                    width: 80,
                    backgroundColor: "inherit",
                    color: "white",
                  }}
                  mode="horizontal"
                  items={items}
                />
              </div>

              {/* Mobile: avatar (visual only) + three-dots menu */}
              <div className="flex lg:hidden items-center gap-2">
                <Avatar style={{ backgroundColor: "#f56a00" }}>
                  {capitalizeFirst(
                    user?.firstname
                      ? user.firstname.charAt(0)
                      : user.email.charAt(0),
                  )}
                </Avatar>
                <Dropdown
                  menu={{ items: mobileMenuItems }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined className="text-xl" />}
                  />
                </Dropdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
